import { gql, useQuery } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconMetrics from '@apollo/icons/default/IconMetrics.svg';
import {
  Button,
  ButtonGroup,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerTitle,
} from '@apollo/orbit';
import classnames from 'classnames';
import React from 'react';
import { useHistory } from 'react-router';

import { OperationTypeBadge } from 'src/components/common/operationTypeBadge/OperationTypeBadge';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useMermaidRender } from 'src/components/queryPlan/mermaid/Mermaid';
import { queryPlanToMermaid } from 'src/components/queryPlan/queryPlanToMermaid';
import { usePublishedSubgraphs } from 'src/hooks/usePublishedSubgraphs';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayClosedLocation } from 'src/lib/routing';

import { SafeQueryPlan } from '../../explorerPage/helpers/queryPlanHelpers';

import { OperationSignature } from './OperationSignature';
import { OperationSignatureActions } from './OperationSignatureActions';
import { OperationSignatureTooltip } from './OperationSignatureTooltip';
import { QueryPlanActions } from './QueryPlanActions';
import { QueryPlanTooltip } from './QueryPlanTooltip';
import { QueryPlanWindow } from './QueryPlanWindow';

export const OperationDetailsSignatureQuery = gql<
  GraphQLTypes.OperationDetailsSignatureQuery,
  GraphQLTypes.OperationDetailsSignatureQueryVariables
>`
  query OperationDetailsSignatureQuery($serviceId: ID!, $operationId: ID!) {
    service(id: $serviceId) {
      id
      operation(id: $operationId) {
        id
        signature
      }
    }
  }
`;

export const OperationDetailsQueryPlanQuery = gql<
  GraphQLTypes.OperationDetailsQueryPlanQuery,
  GraphQLTypes.OperationDetailsQueryPlanQueryVariables
>`
  query OperationDetailsQueryPlanQuery(
    $serviceId: ID!
    $variant: String!
    $document: GraphQLDocument!
    $includePlan: Boolean!
  ) {
    service(id: $serviceId) {
      id
      variant(name: $variant) {
        id
        plan(document: $document) @include(if: $includePlan) {
          object
          text
        }
        compositionVersion
      }
    }
  }
`;

type OperationDetailsDrawerProps = {
  graphId: string;
  graphVariant?: string;
  operationId?: string;
  queryName?: string | null;
  overlay?: string;
};

export function OperationDetailsDrawer(props: OperationDetailsDrawerProps) {
  const { graphId, graphVariant, operationId, queryName, overlay } = props;

  const history = useHistory();
  const [mermaidView, setMermaidView] = React.useState(true);
  const [showQueryPlan, setShowQueryPlan] = React.useState(false);

  const {
    loading: signatureLoading,
    error: signatureError,
    data: signatureData,
  } = useQuery(OperationDetailsSignatureQuery, {
    variables: {
      serviceId: graphId,
      operationId: operationId ?? '',
    },
    skip: !graphId || !operationId,
  });

  const signature = signatureData?.service?.operation?.signature ?? '';

  const {
    loading: queryPlanLoading,
    error: queryPlanError,
    data: queryPlanData,
  } = useQuery(OperationDetailsQueryPlanQuery, {
    variables: {
      serviceId: graphId,
      variant: graphVariant ?? '',
      document: signature,
      includePlan: Boolean(signature),
    },
    skip: !graphId || !graphVariant,
  });

  const { subgraphMap } = usePublishedSubgraphs({
    graphId,
    graphVariant,
  });

  const compositionVersion =
    queryPlanData?.service?.variant?.compositionVersion;
  const planText = queryPlanData?.service?.variant?.plan?.text;
  const planObject = queryPlanData?.service?.variant?.plan?.object;
  const safeQueryPlan = planObject ? (planObject as SafeQueryPlan) : undefined;

  const mermaidPlanNodeString = React.useMemo(() => {
    if (!safeQueryPlan?.node) return '';
    return String(queryPlanToMermaid(safeQueryPlan));
  }, [safeQueryPlan]);

  const queryPlanSVGText = useMermaidRender(mermaidPlanNodeString);

  return (
    <Drawer
      onClose={() => {
        history.replace(setOverlayClosedLocation(history.location));
      }}
      isOpen={overlay === 'operation-details'}
      size="lg"
      autoFocus={false}
    >
      <DrawerOverlay />
      <DrawerContent className="m-0 lg:m-6 lg:rounded">
        <DrawerHeader className="justify-between">
          <div className="flex flex-1 items-center gap-3">
            <IconMetrics className="text-icon-secondary" />
            <DrawerTitle
              className={classnames(
                'max-w-60 truncate',
                !queryName && 'italic',
              )}
            >
              {queryName || 'Unnamed Operation'}
            </DrawerTitle>
            <OperationTypeBadge signature={signature} />
            <Tooltip
              className="font-normal"
              label={
                showQueryPlan ? (
                  <QueryPlanTooltip compositionVersion={compositionVersion} />
                ) : (
                  <OperationSignatureTooltip />
                )
              }
              interactive
            >
              <IconInfo className="size-4 text-icon-secondary" />
            </Tooltip>
          </div>
          <ButtonGroup>
            {showQueryPlan ? (
              <QueryPlanActions
                mermaidView={mermaidView}
                setMermaidView={setMermaidView}
                queryPlanSVGText={queryPlanSVGText}
              />
            ) : (
              <OperationSignatureActions
                location={history.location}
                graphId={graphId}
                graphVariant={graphVariant}
                signature={signature}
              />
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowQueryPlan(!showQueryPlan)}
            >
              Switch to {showQueryPlan ? 'Signature' : 'Query Plan'}
            </Button>
          </ButtonGroup>
        </DrawerHeader>

        <DrawerBody
          className={classnames(showQueryPlan && !mermaidView ? 'p-0' : null)}
        >
          {!showQueryPlan ? (
            <OperationSignature
              name={queryName ?? ''}
              loading={signatureLoading}
              error={!!signatureError}
              signature={signature}
            />
          ) : (
            <QueryPlanWindow
              mermaidView={mermaidView}
              graphId={graphId}
              graphVariant={graphVariant}
              error={queryPlanError}
              loading={queryPlanLoading}
              mermaidPlanNodeString={mermaidPlanNodeString}
              planText={planText}
              queryPlan={safeQueryPlan}
              subgraphMap={subgraphMap}
            />
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
